import SeoHead from '../../components/feature/SeoHead';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Vender Coche en Barcelona | Guía Completa 2025 | Desguace Barcelona',
  description: 'Guía completa para vender tu coche en Barcelona al mejor precio. Coches siniestrados, averiados, sin ITV. Tasación gratuita, recogida gratis y pago inmediato.',
  url: `${siteUrl}/vender-coche-barcelona`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Vender Coche Barcelona', item: `${siteUrl}/vender-coche-barcelona` },
    ],
  },
};

export default function VenderCochePage() {
  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Vender Coche en Barcelona | Guía Completa 2025 | Desguace Barcelona"
        description="Guía completa para vender tu coche en Barcelona al mejor precio. Coches siniestrados, averiados, sin ITV. Tasación gratuita, recogida gratis y pago inmediato."
        keywords="vender coche Barcelona, vender coche siniestrado, vender coche averiado, desguace Barcelona, compra coches Barcelona"
        canonical={`${siteUrl}/vender-coche-barcelona`}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#1a2332] to-[#2d3e50]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              Vender Coche en Barcelona: Guía Completa 2025
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Todo lo que necesitas saber para vender tu coche al mejor precio en Barcelona y provincia
            </p>
          </div>
        </section>

        {/* Contenido Principal SEO */}
        <article className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Introducción */}
            <div className="prose prose-lg max-w-none mb-12">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                <strong>Vender un coche en Barcelona</strong> puede ser un proceso complicado si no conoces las opciones disponibles. Ya sea que tengas un vehículo siniestrado, averiado, sin ITV o simplemente quieras deshacerte de tu coche viejo, en esta guía encontrarás toda la información necesaria para tomar la mejor decisión y obtener el máximo valor por tu vehículo.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                En <strong>Desguace Barcelona</strong> llevamos más de 15 años ayudando a miles de personas a vender sus coches de forma rápida, legal y al mejor precio del mercado. Hemos comprado más de 15.000 vehículos en Barcelona y provincia, convirtiéndonos en el desguace de referencia en Cataluña.
              </p>
            </div>

            {/* Sección 1 */}
            <section className="mb-16">
              <h2 className="text-3xl font-black text-[#1a2332] mb-6">
                ¿Por Qué Vender tu Coche a un Desguace en Barcelona?
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Vender tu coche a un desguace profesional en Barcelona ofrece múltiples ventajas frente a otras opciones como la venta a particulares o concesionarios. Estas son las principales razones por las que miles de barceloneses eligen esta opción cada año:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-[#f8f9fa] p-6 rounded-xl">
                  <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center mb-4">
                    <i className="ri-time-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-3">Rapidez Garantizada</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Tasación en menos de 24 horas y recogida en 48-72 horas. No necesitas esperar semanas o meses como en la venta a particulares.
                  </p>
                </div>

                <div className="bg-[#f8f9fa] p-6 rounded-xl">
                  <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center mb-4">
                    <i className="ri-shield-check-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-3">Proceso 100% Legal</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Nos encargamos de todos los trámites: certificado de destrucción, baja en Tráfico y gestión completa de documentación.
                  </p>
                </div>

                <div className="bg-[#f8f9fa] p-6 rounded-xl">
                  <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center mb-4">
                    <i className="ri-money-euro-circle-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-3">Pago Inmediato</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Recibes el dinero al momento de la recogida. Sin esperas, sin cheques que cobrar, sin complicaciones.
                  </p>
                </div>

                <div className="bg-[#f8f9fa] p-6 rounded-xl">
                  <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center mb-4">
                    <i className="ri-truck-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-3">Recogida Gratuita</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Vamos a buscar tu coche donde estés en Barcelona y provincia. Sin costes de grúa ni desplazamientos.
                  </p>
                </div>
              </div>

              <p className="text-gray-700 leading-relaxed">
                Además, compramos coches en cualquier estado: siniestrados, averiados, sin ITV, con problemas mecánicos graves o simplemente vehículos viejos que ya no quieres. No importa la marca, modelo o año, siempre hacemos una oferta justa y competitiva.
              </p>
            </section>

            {/* Sección 2 */}
            <section className="mb-16">
              <h2 className="text-3xl font-black text-[#1a2332] mb-6">
                Tipos de Coches que Compramos en Barcelona
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                En Desguace Barcelona compramos todo tipo de vehículos, independientemente de su estado o condición. Nuestra experiencia de más de 15 años nos permite valorar correctamente cualquier coche y ofrecer el mejor precio del mercado.
              </p>

              <div className="space-y-6">
                <div className="border-l-4 border-[#ff6b35] pl-6 py-2">
                  <h3 className="text-2xl font-bold text-[#1a2332] mb-3">Coches Siniestrados en Barcelona</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Si has tenido un accidente y tu coche ha sido declarado siniestro total por la aseguradora, somos tu mejor opción. Compramos vehículos con daños graves, ya sean frontales, laterales, traseros o múltiples. No importa si el coche está en un taller, en tu garaje o en la vía pública, nosotros lo recogemos.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Ofrecemos el mejor precio por coches siniestrados en Barcelona, superando habitualmente las ofertas de las aseguradoras. Además, gestionamos toda la documentación necesaria para que no tengas que preocuparte de nada.
                  </p>
                </div>

                <div className="border-l-4 border-[#ff6b35] pl-6 py-2">
                  <h3 className="text-2xl font-bold text-[#1a2332] mb-3">Coches Averiados en Barcelona</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    ¿Tu coche tiene una avería grave y la reparación es más cara que el valor del vehículo? Esta es una situación muy común en Barcelona. Compramos coches con averías mecánicas, eléctricas, de motor, caja de cambios, o cualquier otro problema técnico.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    No necesitas reparar nada antes de venderlo. Evaluamos el vehículo tal como está y te hacemos una oferta justa basada en el valor de las piezas y materiales recuperables.
                  </p>
                </div>

                <div className="border-l-4 border-[#ff6b35] pl-6 py-2">
                  <h3 className="text-2xl font-bold text-[#1a2332] mb-3">Coches Sin ITV en Barcelona</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Muchos propietarios en Barcelona tienen coches que llevan años sin pasar la ITV. Ya sea porque el vehículo no puede superar la inspección o simplemente porque dejó de usarse, compramos coches sin ITV sin ningún problema.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Nos encargamos de todos los trámites legales, incluida la baja definitiva en Tráfico. No tendrás que preocuparte por multas, impuestos o sanciones futuras.
                  </p>
                </div>

                <div className="border-l-4 border-[#ff6b35] pl-6 py-2">
                  <h3 className="text-2xl font-bold text-[#1a2332] mb-3">Coches Viejos y de Baja Gama</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Si tienes un coche viejo que ya no usas y ocupa espacio en tu garaje o parking, podemos ayudarte. Compramos vehículos antiguos, de baja gama o con muchos kilómetros que son difíciles de vender a particulares o concesionarios.
                  </p>
                </div>
              </div>
            </section>

            {/* Sección 3 */}
            <section className="mb-16 bg-[#f8f9fa] p-8 rounded-2xl">
              <h2 className="text-3xl font-black text-[#1a2332] mb-6">
                Proceso Completo para Vender tu Coche en Barcelona
              </h2>
              <p className="text-gray-700 leading-relaxed mb-8">
                Hemos diseñado un proceso simple y transparente para que vendas tu coche de la forma más cómoda posible. Estos son los pasos que seguirás:
              </p>

              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1a2332] text-white rounded-full flex items-center justify-center font-black text-lg">
                      1
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-[#1a2332] mb-2">Solicita tu Tasación Gratuita</h4>
                      <p className="text-gray-600 leading-relaxed">
                        Rellena nuestro formulario online con los datos básicos de tu vehículo: matrícula, marca, modelo y estado general. También puedes llamarnos directamente por teléfono. El proceso toma menos de 2 minutos.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1a2332] text-white rounded-full flex items-center justify-center font-black text-lg">
                      2
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-[#1a2332] mb-2">Recibe tu Oferta en 24 Horas</h4>
                      <p className="text-gray-600 leading-relaxed">
                        Nuestro equipo de expertos evaluará tu vehículo y te enviará una oferta competitiva en menos de 24 horas. La oferta es sin compromiso, no tienes obligación de aceptarla. Somos transparentes con nuestros precios y explicamos cómo llegamos a la valoración.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1a2332] text-white rounded-full flex items-center justify-center font-black text-lg">
                      3
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-[#1a2332] mb-2">Acordamos Recogida del Vehículo</h4>
                      <p className="text-gray-600 leading-relaxed">
                        Si aceptas la oferta, coordinamos día y hora para recoger tu coche. Trabajamos en toda Barcelona y provincia: Barcelona ciudad, Badalona, L'Hospitalet, Sabadell, Terrassa, Mataró, Granollers, Vilafranca y alrededores. La recogida es completamente gratuita.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1a2332] text-white rounded-full flex items-center justify-center font-black text-lg">
                      4
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-[#1a2332] mb-2">Verificación y Pago Inmediato</h4>
                      <p className="text-gray-600 leading-relaxed">
                        El día de la recogida, verificamos que el vehículo corresponde con la descripción y revisamos la documentación. Una vez todo está correcto, realizamos el pago inmediato mediante transferencia bancaria o efectivo, según prefieras. Recibes el dinero en el momento.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1a2332] text-white rounded-full flex items-center justify-center font-black text-lg">
                      5
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-[#1a2332] mb-2">Gestión de Trámites Legales</h4>
                      <p className="text-gray-600 leading-relaxed">
                        Nosotros nos encargamos de todo el papeleo: certificado de destrucción, baja definitiva en la DGT, notificación a tu aseguradora y cualquier otro trámite necesario. Recibirás toda la documentación legal que acredita que el vehículo ha sido dado de baja correctamente.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Sección 4 */}
            <section className="mb-16">
              <h2 className="text-3xl font-black text-[#1a2332] mb-6">
                Documentación Necesaria para Vender tu Coche
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Para vender tu coche a un desguace en Barcelona necesitas tener preparada la siguiente documentación. No te preocupes si falta algún documento, podemos asesorarte sobre cómo obtenerlo:
              </p>

              <div className="bg-white border-2 border-[#1a2332] rounded-xl p-8">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] text-2xl flex-shrink-0"></i>
                    <div>
                      <strong className="text-[#1a2332]">DNI o NIE del titular:</strong>
                      <span className="text-gray-700"> Documento de identidad vigente de la persona que figura como propietaria en el permiso de circulación.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] text-2xl flex-shrink-0"></i>
                    <div>
                      <strong className="text-[#1a2332]">Permiso de circulación:</strong>
                      <span className="text-gray-700"> El documento verde que acredita que el vehículo puede circular por vías públicas.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] text-2xl flex-shrink-0"></i>
                    <div>
                      <strong className="text-[#1a2332]">Ficha técnica del vehículo:</strong>
                      <span className="text-gray-700"> Documento con las características técnicas del coche.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] text-2xl flex-shrink-0"></i>
                    <div>
                      <strong className="text-[#1a2332]">Último recibo del impuesto de circulación (opcional):</strong>
                      <span className="text-gray-700"> Aunque no es imprescindible, ayuda a agilizar el proceso.</span>
                    </div>
                  </li>
                </ul>

                <div className="mt-6 p-4 bg-[#f8f9fa] rounded-lg">
                  <p className="text-sm text-gray-700">
                    <i className="ri-information-line text-[#ff6b35] mr-2"></i>
                    <strong>Nota importante:</strong> Si el vehículo tiene cargas, embargos o multas pendientes, podemos asesorarte sobre cómo proceder. En algunos casos aún es posible vender el coche.
                  </p>
                </div>
              </div>
            </section>

            {/* Sección 5 */}
            <section className="mb-16">
              <h2 className="text-3xl font-black text-[#1a2332] mb-6">
                Zonas de Barcelona Donde Operamos
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Ofrecemos servicio de recogida gratuita en toda Barcelona y provincia. Estas son las principales zonas donde trabajamos:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-[#f8f9fa] p-4 rounded-lg">
                  <h4 className="font-bold text-[#1a2332] mb-2 flex items-center">
                    <i className="ri-map-pin-line text-[#ff6b35] mr-2"></i>
                    Barcelona Capital
                  </h4>
                  <p className="text-sm text-gray-600">Todos los barrios y distritos de la ciudad</p>
                </div>
                <div className="bg-[#f8f9fa] p-4 rounded-lg">
                  <h4 className="font-bold text-[#1a2332] mb-2 flex items-center">
                    <i className="ri-map-pin-line text-[#ff6b35] mr-2"></i>
                    Badalona
                  </h4>
                  <p className="text-sm text-gray-600">Recogida en toda la ciudad</p>
                </div>
                <div className="bg-[#f8f9fa] p-4 rounded-lg">
                  <h4 className="font-bold text-[#1a2332] mb-2 flex items-center">
                    <i className="ri-map-pin-line text-[#ff6b35] mr-2"></i>
                    L'Hospitalet
                  </h4>
                  <p className="text-sm text-gray-600">Servicio completo en la zona</p>
                </div>
                <div className="bg-[#f8f9fa] p-4 rounded-lg">
                  <h4 className="font-bold text-[#1a2332] mb-2 flex items-center">
                    <i className="ri-map-pin-line text-[#ff6b35] mr-2"></i>
                    Sabadell
                  </h4>
                  <p className="text-sm text-gray-600">Cobertura total del municipio</p>
                </div>
                <div className="bg-[#f8f9fa] p-4 rounded-lg">
                  <h4 className="font-bold text-[#1a2332] mb-2 flex items-center">
                    <i className="ri-map-pin-line text-[#ff6b35] mr-2"></i>
                    Terrassa
                  </h4>
                  <p className="text-sm text-gray-600">Recogida en toda la ciudad</p>
                </div>
                <div className="bg-[#f8f9fa] p-4 rounded-lg">
                  <h4 className="font-bold text-[#1a2332] mb-2 flex items-center">
                    <i className="ri-map-pin-line text-[#ff6b35] mr-2"></i>
                    Mataró
                  </h4>
                  <p className="text-sm text-gray-600">Servicio completo disponible</p>
                </div>
                <div className="bg-[#f8f9fa] p-4 rounded-lg">
                  <h4 className="font-bold text-[#1a2332] mb-2 flex items-center">
                    <i className="ri-map-pin-line text-[#ff6b35] mr-2"></i>
                    Granollers
                  </h4>
                  <p className="text-sm text-gray-600">Cobertura en toda la zona</p>
                </div>
                <div className="bg-[#f8f9fa] p-4 rounded-lg">
                  <h4 className="font-bold text-[#1a2332] mb-2 flex items-center">
                    <i className="ri-map-pin-line text-[#ff6b35] mr-2"></i>
                    Vilafranca
                  </h4>
                  <p className="text-sm text-gray-600">Recogida disponible</p>
                </div>
                <div className="bg-[#f8f9fa] p-4 rounded-lg">
                  <h4 className="font-bold text-[#1a2332] mb-2 flex items-center">
                    <i className="ri-map-pin-line text-[#ff6b35] mr-2"></i>
                    Provincia
                  </h4>
                  <p className="text-sm text-gray-600">Consulta tu zona específica</p>
                </div>
              </div>
            </section>

            {/* CTA Final */}
            <section className="bg-gradient-to-br from-[#1a2332] to-[#2d3e50] rounded-2xl p-12 text-center text-white">
              <h2 className="text-4xl font-black mb-4">
                ¿Listo para Vender tu Coche en Barcelona?
              </h2>
              <p className="text-xl mb-8 text-white/90">
                Obtén tu tasación gratuita en menos de 24 horas
              </p>
              <a 
                href="https://wa.me/34932932913?text=Hola%20Desguaces%20Barcelona%2C%20quiero%20valorar%20mi%20coche" 
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center px-10 py-5 bg-[#25d366] text-white rounded-full font-bold text-lg hover:bg-[#1da851] transition-all shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line mr-3 text-2xl"></i>
                WhatsApp 932 932 913
              </a>
            </section>

          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
